import React, { useState } from 'react';
import styled from 'styled-components';
import { MdDeleteForever, MdPeopleOutline, MdPersonOff, MdDangerous } from 'react-icons/md';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  padding: 16px;
  backdrop-filter: blur(2px);
`;

const ModalCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.xl};
  padding: 32px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  animation: modalIn 0.2s ease-out;

  @keyframes modalIn {
    from { opacity: 0; transform: scale(0.94) translateY(-8px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
`;

const IconBox = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  background: rgba(252, 129, 129, 0.15);

  svg {
    font-size: 1.75rem;
    color: ${p => p.theme.colors.danger};
  }
`;

const Title = styled.h3`
  font-size: 1.0625rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 8px;
`;

const Message = styled.p`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 20px;
  line-height: 1.6;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 26px;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid ${p => p.selected
    ? (p.danger ? p.theme.colors.danger : p.theme.colors.primary)
    : p.theme.colors.border};
  background: ${p => p.selected
    ? (p.danger ? 'rgba(252, 129, 129, 0.08)' : `${p.theme.colors.primary}0d`)
    : 'transparent'};
  cursor: pointer;
  transition: all 0.15s ease;

  input { margin-top: 3px; flex-shrink: 0; cursor: pointer; }

  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
    margin-top: 1px;
    color: ${p => p.selected
      ? (p.danger ? p.theme.colors.danger : p.theme.colors.primary)
      : p.theme.colors.textLight};
  }
`;

const OptionBody = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const OptionDesc = styled.div`
  font-size: 0.78rem;
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 2px;
  line-height: 1.4;
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Btn = styled.button`
  padding: 9px 22px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  ${p => p.variant === 'cancel' && `
    background: ${p.theme.colors.backgroundDark};
    color: ${p.theme.colors.textSecondary};
    &:hover { background: ${p.theme.colors.border}; color: ${p.theme.colors.textPrimary}; }
  `}

  ${p => p.variant === 'confirm' && `
    background: linear-gradient(135deg, ${p.danger ? p.theme.colors.danger : p.theme.colors.primary}, ${p.danger ? '#E53E3E' : p.theme.colors.primaryDark});
    color: white;
    box-shadow: 0 4px 12px ${p.danger ? p.theme.colors.danger : p.theme.colors.primary}40;
    &:hover { transform: translateY(-1px); }
  `}
`;

const OPTIONS = [
  {
    value: 'normal',
    icon: <MdPeopleOutline />,
    title: 'Dejarlos como están',
    desc: 'Solo se desvinculan del grupo; sus datos no cambian.',
  },
  {
    value: 'desactivar',
    icon: <MdPersonOff />,
    title: 'Desactivarlos',
    desc: 'Se conservan sus datos, pero quedan marcados como Inactivo.',
  },
  {
    value: 'eliminar',
    icon: <MdDangerous />,
    title: 'Eliminarlos también',
    desc: 'Se borran permanentemente junto con el grupo. No se puede deshacer.',
    danger: true,
  },
];

const CONFIRM_LABEL = {
  normal: 'Eliminar grupo',
  desactivar: 'Eliminar grupo y desactivar clientes',
  eliminar: 'Eliminar grupo y clientes',
};

const DeleteGroupModal = ({ grupo, onConfirm, onCancel }) => {
  const [accion, setAccion] = useState('normal');
  const totalClientes = grupo.total_clientes ?? 0;

  return (
    <Overlay onClick={onCancel}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <IconBox><MdDeleteForever /></IconBox>
        <Title>Eliminar grupo "{grupo.nombre}"</Title>
        <Message>
          {totalClientes > 0
            ? `Este grupo tiene ${totalClientes} cliente${totalClientes === 1 ? '' : 's'}. ¿Qué quieres hacer con ${totalClientes === 1 ? 'él' : 'ellos'}?`
            : 'Este grupo no tiene clientes. Se eliminará directamente.'}
        </Message>

        {totalClientes > 0 && (
          <OptionsList>
            {OPTIONS.map(opt => (
              <OptionRow key={opt.value} selected={accion === opt.value} danger={opt.danger}>
                <input
                  type="radio"
                  name="accion-grupo"
                  value={opt.value}
                  checked={accion === opt.value}
                  onChange={() => setAccion(opt.value)}
                />
                {opt.icon}
                <OptionBody>
                  <OptionTitle>{opt.title}</OptionTitle>
                  <OptionDesc>{opt.desc}</OptionDesc>
                </OptionBody>
              </OptionRow>
            ))}
          </OptionsList>
        )}

        <Buttons>
          <Btn variant="cancel" onClick={onCancel}>Cancelar</Btn>
          <Btn variant="confirm" danger={accion === 'eliminar'} onClick={() => onConfirm(accion)}>
            {totalClientes > 0 ? CONFIRM_LABEL[accion] : 'Eliminar grupo'}
          </Btn>
        </Buttons>
      </ModalCard>
    </Overlay>
  );
};

export default DeleteGroupModal;
